// Finish notes promises that every line the user wrote survives into the merged notes. The prompt's line check
// catches most dropped lines; stitchUserLines puts back the rest after the model call. Everything here must work in
// any language, so it uses no stopword list, no stemmer, and no language-specific rules except the section names.

interface UserLine {
  text: string // list markers removed
  heading: boolean
  short: boolean
  long: boolean
}

type LineSkipReason = 'heading' | 'short' | 'long' | 'repeat'

export interface StitchStats {
  lineCount: number
  skipped: Record<LineSkipReason, number>
  missingBeforeCount: number
  insertedCount: number
  // Indexes into userLines(), so the finish-notes harness can look up what was inserted without it reaching telemetry.
  insertedLineIndexes: number[]
  skipReason?: 'language' | 'no_match'
}

interface StitchResult {
  content: string
  stats: StitchStats
}

// Markers need trailing whitespace, so "-5 degrees", "#1 priority" and "e. coli" keep their first character.
const LIST_MARKER = /^\s*(?:(?:[-*+•·◦▪–—>]|#+|\d+[.)]|[a-z]\)|\[[ xX]?\])\s+)+/u
const LIST_ITEM = /^\s*(?:[-*+•·◦▪–—>]|\d+[.)]|[a-z]\)|\[[ xX]?\])\s+/u
const HEADING = /^\s*(#{1,6})\s+(.*)$/
const TAG_ONLY = /^<\/?[a-z_]+>$/i
const TERMINAL_PUNCTUATION = /[.!?。！？…]$/u
// A label like "Q3 risks" has a handful of words; a longer unpunctuated line is more likely a thought.
const MAX_HEADING_WORDS = 6
// A paragraph-length line comes back reworded across several bullets, too loosely for word matching to see, and
// pasting it back duplicates the paragraph. On the finish-notes harness the model never dropped one.
const MAX_RESTORED_WORDS = 25
const NO_MATCH_MIN_LINES = 5

// Chinese, Japanese and Thai don't separate words with spaces, so they are compared by character pairs.
const UNSPACED_SCRIPT = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}]/u

function normalize(text: string): string {
  return text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ')
    .trim()
}

function indentOf(raw: string): number {
  return (raw.match(/^\s*/)?.[0] ?? '').replace(/\t/g, '    ').length
}

// Two characters stand in for a word in scripts written without spaces.
function wordCount(norm: string): number {
  return UNSPACED_SCRIPT.test(norm) ? Math.ceil(norm.replace(/ /g, '').length / 2) : norm.split(' ').length
}

// Shape, not vocabulary, so it works in any language. An indented next line that isn't a list item doesn't count:
// handwriting recognition indents the second half of a wrapped line.
function isHeadingLine(raw: string, text: string, next: string | undefined): boolean {
  if (HEADING.test(raw) || /[:：]$/u.test(text)) return true
  if (next === undefined || TERMINAL_PUNCTUATION.test(text) || wordCount(normalize(text)) > MAX_HEADING_WORDS) {
    return false
  }
  return LIST_ITEM.test(next) && (!LIST_ITEM.test(raw) || indentOf(next) > indentOf(raw))
}

export function userLines(userAuthoredText: string): UserLine[] {
  const kept = userAuthoredText
    .split('\n')
    .map((raw) => ({raw, text: raw.replace(LIST_MARKER, '').trim()}))
    .filter(({text}) => /[\p{L}\p{N}]/u.test(text) && !TAG_ONLY.test(text))
  return kept.map(({raw, text}, i) => {
    const words = wordCount(normalize(text))
    return {
      text,
      heading: isHeadingLine(raw, text, kept[i + 1]?.raw),
      short: words <= 2,
      long: words > MAX_RESTORED_WORDS
    }
  })
}

// Inflection without a stemmer: words match on a long enough shared start ("meeting"/"meetings"). Numbers never do.
const MIN_PREFIX = 4
const PREFIX_SHARE = 0.6
// Tuned against the Opus line judge on the finish-notes harness: raising it restores more dropped lines but pastes far
// more of the user's shorthand beside the model's rewording of it, which reads as clutter.
export const PRESENCE_THRESHOLD = 0.4

function tokensMatch(a: string, b: string): boolean {
  if (a === b) return true
  // Both rules below need the first letters to agree; this exits on most pairs before any regex runs.
  if (a[0] !== b[0]) return false
  if (/\p{N}/u.test(a) || /\p{N}/u.test(b)) return false
  if (isAbbreviation(a, b)) return true
  const shorter = Math.min(a.length, b.length)
  let common = 0
  while (common < shorter && a[common] === b[common]) common++
  return common >= Math.max(MIN_PREFIX, Math.ceil(shorter * PREFIX_SHARE))
}

// "expts" for "experiments", "ppl" for "people": the short form's letters appear in order, starting together.
function isAbbreviation(a: string, b: string): boolean {
  const [short, long] = a.length < b.length ? [a, b] : [b, a]
  if (short.length < 3 || short[0] !== long[0]) return false
  let j = 0
  for (const ch of long) if (ch === short[j]) j++
  return j === short.length
}

function bigrams(norm: string): string[] {
  const s = norm.replace(/ /g, '')
  if (s.length < 2) return [s]
  return Array.from({length: s.length - 1}, (_, i) => s.slice(i, i + 2))
}

function features(norm: string): string[] {
  return UNSPACED_SCRIPT.test(norm) ? bigrams(norm) : norm.split(' ').filter(Boolean)
}

// The model may spread one user line over several bullets, the more so the longer the line: a reviewer's paragraph
// becomes four or five. So a line is matched against runs of consecutive output lines, longer for longer lines.
const WORDS_PER_OUTPUT_LINE = 12
const MAX_SPAN = 8

function spanFor(lineFeatureCount: number): number {
  return Math.min(MAX_SPAN, Math.max(2, Math.ceil(lineFeatureCount / WORDS_PER_OUTPUT_LINE)))
}

interface NotesIndex {
  blank: boolean[]
  nonBlankCount: number
  linesByFeature: Map<string, number[]>
  featuresByFirstChar: Map<string, string[]>
  // Per feature a user line asks about: running count of the output lines carrying it, so any run of lines is one
  // subtraction. A re-run over earlier notes brings hundreds of user lines, and this runs on the job's thread.
  carriers: Map<string, Int32Array>
}

function indexNotes(outputLines: string[]): NotesIndex {
  const linesByFeature = new Map<string, number[]>()
  const blank = outputLines.map((line, i) => {
    const norm = normalize(line)
    for (const f of new Set(features(norm))) {
      const carrying = linesByFeature.get(f)
      if (carrying) carrying.push(i)
      else linesByFeature.set(f, [i])
    }
    return !norm
  })
  const featuresByFirstChar = new Map<string, string[]>()
  for (const f of linesByFeature.keys()) {
    const sameStart = featuresByFirstChar.get(f[0] ?? '')
    if (sameStart) sameStart.push(f)
    else featuresByFirstChar.set(f[0] ?? '', [f])
  }
  return {
    blank,
    nonBlankCount: blank.filter((b) => !b).length,
    linesByFeature,
    featuresByFirstChar,
    carriers: new Map()
  }
}

function carriersOf(f: string, index: NotesIndex, unspaced: boolean): Int32Array {
  const cached = index.carriers.get(f)
  if (cached) return cached
  const carrying = new Set(index.linesByFeature.get(f) ?? [])
  if (!unspaced) {
    for (const other of index.featuresByFirstChar.get(f[0] ?? '') ?? []) {
      if (other !== f && tokensMatch(f, other)) {
        for (const line of index.linesByFeature.get(other) ?? []) carrying.add(line)
      }
    }
  }
  const counts = new Int32Array(index.blank.length + 1)
  index.blank.forEach((_, i) => {
    counts[i + 1] = (counts[i] ?? 0) + (carrying.has(i) ? 1 : 0)
  })
  index.carriers.set(f, counts)
  return counts
}

// Without a stopword list, a word's weight comes from how many output lines carry it: "the" or "de" is on most
// lines and says little about where a user line went; a name or a term is on few and says a lot.
function rarity(counts: Int32Array, index: NotesIndex): number {
  return Math.log((index.nonBlankCount + 1) / ((counts[counts.length - 1] ?? 0) + 1)) + 1
}

function bestMatch(text: string, index: NotesIndex): {score: number; outputLine: number} {
  const norm = normalize(text)
  const lineFeatures = features(norm)
  const unspaced = UNSPACED_SCRIPT.test(norm)
  let best = {score: 0, outputLine: -1}
  if (!lineFeatures.length) return best
  const counts = lineFeatures.map((f) => carriersOf(f, index, unspaced))
  const weights = counts.map((c) => rarity(c, index))
  const total = weights.reduce((a, b) => a + b, 0)
  const maxSpan = spanFor(unspaced ? Math.ceil(lineFeatures.length / 2) : lineFeatures.length)
  index.blank.forEach((isBlank, last) => {
    if (isBlank) return
    for (let first = last; first > last - maxSpan && first >= 0 && !index.blank[first]; first--) {
      let hit = 0
      counts.forEach((c, k) => {
        if ((c[last + 1] ?? 0) - (c[first] ?? 0) > 0) hit += weights[k] ?? 0
      })
      if (hit / total > best.score) best = {score: hit / total, outputLine: last}
    }
  })
  return best
}

export function presenceScore(text: string, notes: string): number {
  return bestMatch(text, indexNotes(notes.split('\n'))).score
}

const BULLET = /^(\s*)(?:[-*+]|\d+[.)])\s/

// Where an aside must never land: it would read as a task. Matched as substrings of the normalized heading, so
// "Wyatt's to-dos" and "Action items" both count. Best effort beyond the languages listed.
const ACTION_SECTION =
  /(?<![\p{L}\p{N}])(?:action items?|actions?|to dos?|todos?|next steps|homework|reminders|to follow up|follow ups?|tasks?|acciones|tareas|pendientes|pr[oó]ximos pasos|deberes|[aà] faire|prochaines [eé]tapes|t[aâ]ches|devoirs|aufgaben|n[aä]chste schritte|ma[sß]nahmen|hausaufgaben|itens de a[cç][aã]o|a[cç][oõ]es|tarefas|pr[oó]ximos passos|pend[eê]ncias|azioni|attivit[aà]|prossimi passi|compiti|actiepunten|acties|taken|volgende stappen|huiswerk|opgaver|n[aæ]ste skridt|oppgaver|uppgifter|n[aä]sta steg|g[oö]revler|yap[iı]lacaklar|задачи|действия|следующие шаги|завдання|дії|наступні кроки|việc cần làm|hành động|tindakan|tugas|langkah selanjutnya)(?![\p{L}\p{N}])|アクション|タスク|宿題|次のステップ|行动项|待办|下一步|作业|行動項目|待辦|할 일|액션|다음 단계|과제|สิ่งที่ต้องทำ/u

// Heading names the model may already have used for leftover lines, in any language the notes are in. The stitch's own
// heading, in the notes' language, comes from the caller.
const MISCELLANEOUS_NAMES = new Set(
  ['Miscellaneous', 'Misc', 'Other', 'Otros', 'Outros', 'Autres', 'Verschiedenes', 'Misceláneos'].map(normalize)
)
// The model's closing Private section stays last; a Miscellaneous section the stitch adds goes before it.
const PRIVATE_NAMES = new Set(['Private', 'Privado', 'Privada', 'Privé', 'Privée', 'Privat', 'Privato'].map(normalize))

function headingAt(line: string): {level: number; name: string} | undefined {
  const m = line.match(HEADING)
  return m ? {level: m[1]?.length ?? 1, name: normalize(m[2] ?? '')} : undefined
}

function actionLines(outputLines: string[]): Set<number> {
  const inAction = new Set<number>()
  const open: {level: number; action: boolean}[] = []
  outputLines.forEach((line, i) => {
    const h = headingAt(line)
    if (h) {
      while (open.length && (open[open.length - 1]?.level ?? 0) >= h.level) open.pop()
      open.push({level: h.level, action: ACTION_SECTION.test(h.name)})
    }
    // The heading too: a line placed right under "# Action items" would read as its first item.
    if (open.some((o) => o.action)) inAction.add(i)
  })
  return inAction
}

// A bullet's own sub-bullets and wrapped lines stay with it; an insert goes after them.
function blockEnd(outputLines: string[], line: number): number {
  const indent = indentOf(outputLines[line] ?? '')
  let end = line
  while (end + 1 < outputLines.length) {
    const next = outputLines[end + 1] ?? ''
    if (!next.trim() || HEADING.test(next) || indentOf(next) <= indent) break
    end++
  }
  return end
}

// Code and display-math blocks, as [opening line, closing line]. A line spliced into one breaks the code or equation.
function fencedBlocks(outputLines: string[]): [number, number][] {
  const blocks: [number, number][] = []
  let open: {line: number; kind: 'code' | 'math'} | undefined
  outputLines.forEach((line, i) => {
    const t = line.trim()
    // An odd number of `$$` opens or closes display math; `$$x$$`, alone or inside a bullet, is already closed.
    const kind = /^(`{3}|~{3})/.test(t) ? 'code' : t.split('$$').length % 2 === 0 ? 'math' : undefined
    if (!kind) return
    if (!open) open = {line: i, kind}
    else if (open.kind === kind) {
      blocks.push([open.line, i])
      open = undefined
    }
  })
  if (open) blocks.push([open.line, outputLines.length - 1])
  return blocks
}

// A splice position inside a block moves out of it: past the block when following an earlier line, ahead of it when
// preceding a later one.
function outsideBlocks(position: number, blocks: [number, number][], ahead: boolean): number {
  const block = blocks.find(([open, close]) => open < position && position <= close)
  return block ? (ahead ? block[0] : block[1] + 1) : position
}

// Bold, because bold in Finish notes marks the user's own words.
function stitchedBullet(text: string, carrier: string | undefined): string {
  const indent = carrier?.match(BULLET)?.[1] ?? ''
  return `${indent}- **${text.replace(/\*\*/g, '')}**`
}

function addToMiscellaneous(outputLines: string[], bullets: string[], heading: string): void {
  const headings = outputLines.map(headingAt)
  const names = new Set([...MISCELLANEOUS_NAMES, normalize(heading)])
  const existing = headings.findIndex((h) => h !== undefined && names.has(h.name))
  if (existing >= 0) {
    const level = headings[existing]?.level ?? 1
    let end = existing + 1
    while (end < outputLines.length && (headings[end]?.level ?? Infinity) > level) end++
    while (end > existing + 1 && !outputLines[end - 1]?.trim()) end--
    outputLines.splice(end, 0, ...bullets)
    return
  }
  const privateAt = headings.findIndex((h) => h?.level === 1 && PRIVATE_NAMES.has(h.name))
  const section = [`# ${heading}`, ...bullets, '']
  if (privateAt >= 0) {
    outputLines.splice(privateAt, 0, ...section)
  } else {
    let end = outputLines.length
    while (end > 0 && !outputLines[end - 1]?.trim()) end--
    outputLines.splice(end, 0, '', ...section.slice(0, -1))
  }
}

export interface StitchOptions {
  // The heading for lines with no surviving neighbor, in the notes' language.
  miscellaneousHeading?: string
  // The user asked for notes in another language, so their lines can't go back verbatim.
  outputLanguageDiffers?: boolean
}

/**
 * Puts back the user's lines that the notes dropped, verbatim and bold, right after the output line carrying the
 * nearest earlier user line that survived, or else just before the one carrying the nearest later one (never inside
 * an action-item list), or else in a Miscellaneous section at the end. Never restores headings, one- or two-word
 * fragments, or paragraph-length lines. Only inserts: what the notes already say is never removed or reordered, and
 * the result is a fixed point, so stitching it again changes nothing.
 */
export function stitchUserLines(notes: string, userAuthoredText: string, options: StitchOptions = {}): StitchResult {
  const lines = userLines(userAuthoredText)
  const stats: StitchStats = {
    lineCount: lines.length,
    skipped: {heading: 0, short: 0, long: 0, repeat: 0},
    missingBeforeCount: 0,
    insertedCount: 0,
    insertedLineIndexes: []
  }
  const seen = new Set<string>()
  const eligible = lines.map((line) => {
    const norm = normalize(line.text)
    const skip = (['heading', 'short', 'long'] as const).find((k) => line[k]) ?? (seen.has(norm) ? 'repeat' : undefined)
    seen.add(norm)
    if (skip) stats.skipped[skip]++
    return !skip
  })
  if (options.outputLanguageDiffers) return {content: notes, stats: {...stats, skipReason: 'language'}}

  const outputLines = notes.split('\n')
  for (let pass = 0; pass <= lines.length; pass++) {
    const index = indexNotes(outputLines)
    const matches = lines.map((line) => bestMatch(line.text, index))
    const present = matches.map((m) => m.score >= PRESENCE_THRESHOLD)
    const missing = lines.map((_, i) => i).filter((i) => eligible[i] && !present[i])
    if (pass === 0) {
      stats.missingBeforeCount = missing.length
      // Not one line found anywhere: the notes are almost surely in another language the detector missed, and every
      // line would be pasted, untranslated, at the end.
      const eligibleCount = eligible.filter(Boolean).length
      if (eligibleCount >= NO_MATCH_MIN_LINES && missing.length === eligibleCount) {
        return {content: notes, stats: {...stats, skipReason: 'no_match'}}
      }
    }
    if (!missing.length) break

    const inAction = actionLines(outputLines)
    const blocks = fencedBlocks(outputLines)
    const isAnchor = (j: number) => present[j] === true && !inAction.has(matches[j]?.outputLine ?? -1)
    // A one- or two-word fragment matches wherever its word first appears, often the header or a takeaway, so it
    // places a line only when no longer user line survived.
    const isPlacedAnchor = (j: number) => isAnchor(j) && !lines[j]?.short
    const canAnchor = lines.some((_, j) => isPlacedAnchor(j)) ? isPlacedAnchor : isAnchor
    const insertsByPosition = new Map<number, {indexes: number[]; carrier: string | undefined}>()
    const toMiscellaneous: number[] = []
    for (const i of missing) {
      let position = -1
      let carrier: string | undefined
      let earlier = i - 1
      while (earlier >= 0 && !canAnchor(earlier)) earlier--
      const later = lines.findIndex((_, j) => j > i && canAnchor(j))
      if (earlier >= 0) {
        const line = matches[earlier]?.outputLine ?? 0
        position = outsideBlocks(blockEnd(outputLines, line) + 1, blocks, false)
        carrier = outputLines[line]
      } else if (later >= 0) {
        // The user's first lines have nothing before them; they go just ahead of what followed them.
        const line = matches[later]?.outputLine ?? 0
        position = outsideBlocks(HEADING.test(outputLines[line] ?? '') ? line + 1 : line, blocks, true)
        carrier = outputLines[line]
      }
      if (position < 0) toMiscellaneous.push(i)
      else insertsByPosition.set(position, {indexes: [...(insertsByPosition.get(position)?.indexes ?? []), i], carrier})
    }
    // Back to front, so earlier positions keep their indexes.
    for (const [position, {indexes, carrier}] of [...insertsByPosition].sort((a, b) => b[0] - a[0])) {
      outputLines.splice(position, 0, ...indexes.map((i) => stitchedBullet(lines[i]?.text ?? '', carrier)))
    }
    if (toMiscellaneous.length) {
      const bullets = toMiscellaneous.map((i) => stitchedBullet(lines[i]?.text ?? '', undefined))
      addToMiscellaneous(outputLines, bullets, options.miscellaneousHeading ?? 'Miscellaneous')
    }
    stats.insertedLineIndexes.push(...missing)
  }
  stats.insertedLineIndexes.sort((a, b) => a - b)
  stats.insertedCount = stats.insertedLineIndexes.length
  return {content: outputLines.join('\n'), stats}
}
